import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { selectCurrentBusinessMode } from '../../app/Reducers/AppConfig';
import { EBusinessType } from '../../app/Reducers/AppConfig/types';
import ProcessingLoading from '../../components/Loader/Processing';
import { useOpenState } from '../../hooks/useOpenState';
import ConnectionsService from '../../utils/Services/Connections';
import ShopifyService from '../../utils/Services/Shopify';
import UserService from '../../utils/Services/User';
import { toastifyGeneralTheme } from '../../utils/ToastifyGeneral';
import { IShopifyStore } from '../Integration/Modals/Shopify';

interface OnboardingContextType {
  isOpen: { [key: string]: boolean };
  userConnections: UserConnectionsInterface[];
  handleIsOpen: (key: string, value: boolean) => void;
  handleOpenClose: (key: string) => void;
  isServiceConnected: (key: string) => boolean;
  handleFinishTutorial: () => void;
  checkServices: (services: string[]) => boolean;
  shopifyStores: IShopifyStore[] | undefined;
}

interface UserConnectionsInterface {
  service: string;
  isConnected: boolean;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export const useOnboardingContext = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      'useOnboardingContext must be used within an OnboardingProvider'
    );
  }
  return context;
};

export const onboardingPages = {
  [EBusinessType.POD]: '/onboarding/pod',
  [EBusinessType.BRANDED]: '/onboarding/branded',
  [EBusinessType.DROPSHIPPING]: '/onboarding/dropshipping',
};
const shopifyService = new ShopifyService();
const OnboardingInitial: React.FC = () => {
  const [isOpen, handleIsOpen, handleOpenClose] = useOpenState({
    printify: false,
    shopify: false,
    isLoading: true,
  });
  const currentBusinessType = useSelector(selectCurrentBusinessMode);
  const navigate = useNavigate();
  const userService = new UserService();
  const connectionService = new ConnectionsService();
  const [userConnections, setUserConnections] = useState<
    UserConnectionsInterface[]
  >([]);
  const [shopifyStores, setShopifyStores] = useState<IShopifyStore[]>();
  const [isFinishingTutorial, setIsFinishingTutorial] = useState(false);
  const isMounted = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Fetch Shopify stores when business type changes
  useEffect(() => {
    if (!currentBusinessType) {
      setShopifyStores([]);
      return;
    }
    shopifyService
      .get_data(`/myShops?businessType=${currentBusinessType}`)
      .then(res => {
        if (isMounted.current) {
          setShopifyStores(res);
        }
      })
      .catch(err => {
        if (isMounted.current) {
          toast.error('Failed to get the shops!', { ...toastifyGeneralTheme });
        }
      })
      .finally(() => {
        if (isMounted.current) {
          handleIsOpen('shopifyShops', false);
        }
      });
  }, [currentBusinessType, handleIsOpen]);

  // Fetch user connections with polling
  useEffect(() => {
    const fetchData = () => {
      if (!currentBusinessType || !isMounted.current) return;

      connectionService
        .get_data(`?businessType=${currentBusinessType}`)
        .then(res => {
          if (isMounted.current) {
            setUserConnections(res.services);
          }
        })
        .catch(err => {
          if (isMounted.current) {
            toast.error(`Failed to get the connection statuses`, {
              ...toastifyGeneralTheme,
            });
          }
        })
        .finally(() => {
          if (isMounted.current) {
            handleIsOpen('isLoading', false);
          }
        });
    };

    // Call the fetchData immediately on mount
    fetchData();

    // Set up the interval to call fetchData every 10 seconds
    const intervalId = setInterval(fetchData, 10000);

    // Clean up the interval on component unmount
    return () => clearInterval(intervalId);
  }, [currentBusinessType, handleIsOpen]);

  const markTutorialAsViewed = () => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);

        if (currentBusinessType) {
          user[currentBusinessType] = true;
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          console.warn(
            'Current business type is null. Cannot mark tutorial as viewed.'
          );
        }
      } catch (error) {
        console.error('Failed to parse user data from localStorage:', error);
      }
    } else {
      console.warn('No user data found in localStorage.');
    }
  };

  const isServiceConnected = useCallback(
    (serviceName: string): boolean => {
      const service = userConnections.find(
        connection => connection.service === serviceName
      );
      return service ? service.isConnected : false;
    },
    [userConnections]
  );

  const handleFinishTutorial = useCallback(async () => {
    if (!currentBusinessType) {
      toast.error('Business type is not selected.', {
        ...toastifyGeneralTheme,
      });
      return;
    }

    if (isFinishingTutorial) {
      return; // Prevent multiple calls
    }

    setIsFinishingTutorial(true);
    handleIsOpen('isLoading', true);

    try {
      await userService.put_data('/onboarding', {
        [currentBusinessType]: true,
      });
      markTutorialAsViewed();

      // Use a small delay to ensure state updates are processed
      setTimeout(() => {
        if (isMounted.current) {
          navigate('/');
        }
      }, 100);
    } catch (err) {
      toast.error(`Failed to end the tutorial`, {
        ...toastifyGeneralTheme,
      });
    } finally {
      if (isMounted.current) {
        handleIsOpen('isLoading', false);
        setIsFinishingTutorial(false);
      }
    }
  }, [currentBusinessType, isFinishingTutorial, handleIsOpen, navigate]);

  const checkServices = useCallback(
    (servicesRequired: string[]): boolean => {
      if (userConnections.length === 0) return true;
      for (const service of userConnections) {
        if (
          servicesRequired.includes(service.service) &&
          !service.isConnected
        ) {
          return true;
        }
      }
      return false;
    },
    [userConnections]
  );

  const contextValue: OnboardingContextType = useMemo(
    () => ({
      isOpen,
      handleIsOpen,
      handleOpenClose,
      isServiceConnected,
      handleFinishTutorial,
      userConnections,
      checkServices,
      shopifyStores,
    }),
    [
      isOpen,
      handleIsOpen,
      handleOpenClose,
      isServiceConnected,
      handleFinishTutorial,
      userConnections,
      checkServices,
      shopifyStores,
    ]
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      <ProcessingLoading isLoading={isOpen.isLoading} />
      <Outlet />
    </OnboardingContext.Provider>
  );
};

export default OnboardingInitial;
